// FilterBar.jsx
import React from "react";
import Input from "../ui/InputForm";
import TimeIntervalSelect from "../ui/TimeIntervalSelect";
import SwitchSelect from "../ui/SwitchSelect";
import SearchableSelect from "../ui/SearchableSelect";
import InfoTooltip from "../ui/InfoTooltip";
import "./FilterBar.css";

function FilterFieldLabel({ children, htmlFor, id, helpText }) {
  return (
    <div className="filter-field-label">
      {htmlFor ? <label htmlFor={htmlFor}>{children}</label> : <span id={id}>{children}</span>}
      {helpText && <InfoTooltip text={helpText} label={`Informatie over ${children}`} />}
    </div>
  );
}

const FALLBACK_OPTIONS = {
  gemeente: [
    { value: "Amsterdam", label: "Amsterdam" },
    { value: "Rotterdam", label: "Rotterdam" },
  ],
  doelgroep: [
    { value: "Ouders", label: "Ouders" },
    { value: "Jongeren", label: "Jongeren" },
  ],
  wanneer: [
    { value: "2026", label: "In 2026" },
    { value: "2025", label: "In 2025" },
    { value: "2024", label: "In 2024" },
  ],
};

function formatLabel(value) {
  if (!value) return "";
  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function ensureCurrentValue(list, value) {
  const currentValues = Array.isArray(value)
    ? value.filter(Boolean).map((item) => String(item))
    : value
      ? [String(value)]
      : [];

  if (currentValues.length === 0) {
    return list;
  }

  const existingValues = new Set(
    list.map((opt) =>
      typeof opt === "string" || typeof opt === "number"
        ? String(opt)
        : String(opt?.value ?? "")
    )
  );

  const extras = currentValues
    .filter((val) => !existingValues.has(val))
    .map((val) => ({
      value: val,
      label: formatLabel(val),
    }));

  if (!extras.length) {
    return list;
  }

  return [...list, ...extras];
}

function getOptionList(options, key, currentValue) {
  const provided = options?.[key];
  if (Array.isArray(provided)) {
    return ensureCurrentValue(provided, currentValue);
  }
  const fallback = FALLBACK_OPTIONS[key] || [];
  return ensureCurrentValue(fallback, currentValue);
}

function FilterBar({
  filtersConfig,
  filters,
  period,
  mode,
  onChange,
  onPeriodChange,
  onModeChange,
  options,
}) {
  return (
    <div className="filter-bar">
      {filtersConfig.includes("aanbieder") && (
        <div className="filter-item search-item">
          <FilterFieldLabel htmlFor="filter-aanbieder-trigger">Aanbieder</FilterFieldLabel>
          <SearchableSelect
            name="filter-aanbieder"
            placeholder="Selecteer aanbieder"
            value={filters.aanbieder}
            options={getOptionList(options, "aanbieders", filters.aanbieder)}
            onChange={(value) => onChange("aanbieder", value)}
            clearLabel="Alle aanbieders"
          />
        </div>
      )}

      {filtersConfig.includes("gemeente") && (
        <div className="filter-item">
          <FilterFieldLabel htmlFor="gemeente" helpText="In welke gemeente woon je?">
            Gemeente
          </FilterFieldLabel>
          <Input
            name="gemeente"
            noLabel
            type="select"
            placeholder="Selecteer gemeente"
            value={filters.gemeente}
            onChange={(e) => onChange("gemeente", e.target.value)}
            options={getOptionList(options, "gemeentes", filters.gemeente)}
            inputClassName="filter-select"
          />
        </div>
      )}

      {filtersConfig.includes("doelgroep") && (
        <div className="filter-item">
          <FilterFieldLabel htmlFor="doelgroep" helpText="Ik ben een jongere of ouder/verzorger">
            Ouders/Jongeren
          </FilterFieldLabel>
          <Input
            name="doelgroep"
            noLabel
            type="select"
            placeholder="Selecteer doelgroep"
            value={filters.doelgroep}
            onChange={(e) => onChange("doelgroep", e.target.value)}
            options={getOptionList(options, "doelgroepen", filters.doelgroep)}
            inputClassName="filter-select"
          />
        </div>
      )}

      {filtersConfig.includes("wanneer") && (
        <div className="filter-item">
          <FilterFieldLabel htmlFor="wanneer-trigger" helpText="Wanneer was deze ervaring?">
            Wanneer ervaring
          </FilterFieldLabel>
          <SearchableSelect
            name="wanneer"
            type="select"
            placeholder="Selecteer jaar"
            value={filters.wanneer}
            onChange={(value) => onChange("wanneer", value)}
            options={getOptionList(options, "wanneer", filters.wanneer)}
            inputClassName="filter-select"
            multiple
            clearLabel="Alle jaren"
          />
        </div>
      )}

      {filtersConfig.includes("jaar") && (
        <div className="filter-item">
          <FilterFieldLabel htmlFor="jaar">Jaar ingevuld</FilterFieldLabel>
          <Input
            name="jaar"
            noLabel
            type="select"
            placeholder="Alle jaren"
            value={filters.jaar || ""}
            onChange={(e) => onChange("jaar", e.target.value)}
            options={getOptionList(options, "jaren", filters.jaar)}
            inputClassName="filter-select"
          />
        </div>
      )}

      {filtersConfig.includes("periode") && (
        <div className="filter-item" role="group" aria-labelledby="filter-periode-label">
          <FilterFieldLabel id="filter-periode-label" helpText="Wanneer heb je de vragenlijst ingevuld?">
            Periode
          </FilterFieldLabel>
          <TimeIntervalSelect value={period} onChange={onPeriodChange} />
        </div>
      )}

      {filtersConfig.includes("weergave") && (
        <div className="filter-item" role="group" aria-labelledby="filter-weergave-label">
          <FilterFieldLabel id="filter-weergave-label" helpText="Absolute of Relatieve weergave">
            Weergave
          </FilterFieldLabel>
          <SwitchSelect
            value={mode}
            onChange={onModeChange}
            options={["abs", "rel"]}
            labels={{ abs: "Absoluut", rel: "Relatief" }}
          />
        </div>
      )}
    </div>
  );
}

export default FilterBar;
